// Email leads tile — job leads from forwarded alert/recruiter email received
// in the last 14 days (the same window as the hunt agenda's new_leads), the
// ones matched to a catalog posting first. Hides itself when there are none
// or the query failed, like PostingAlertsTile.

import { Link } from "react-router";
import { ExternalLink, X } from "lucide-react";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { Pill } from "../ui/Pill";
import { Skeleton } from "../ui/Skeleton";
import { ScorePill } from "../jobs/ScorePill";
import { useDismissLead, useLeads, type JobLead } from "../../lib/leads";

const WINDOW_MS = 14 * 24 * 3600_000;
const MAX_ROWS = 8;

export function LeadsTile() {
  const leads = useLeads();

  if (leads.isLoading) {
    return (
      <Card>
        <CardHeader title="Leads from email" />
        <CardBody className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </CardBody>
      </Card>
    );
  }
  if (leads.error) return null;

  const since = Date.now() - WINDOW_MS;
  const rows = (leads.data?.leads ?? [])
    .filter((l) => l.received_at >= since)
    .sort((a, b) => Number(b.status === "matched") - Number(a.status === "matched") || b.received_at - a.received_at);
  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Leads from email"
        description={`${rows.length} lead${rows.length === 1 ? "" : "s"} from forwarded job alerts and recruiters in the last 14 days.`}
      />
      <CardBody>
        <ul className="space-y-2">
          {rows.slice(0, MAX_ROWS).map((l) => (
            <LeadRow key={l.id} lead={l} />
          ))}
        </ul>
        {rows.length > MAX_ROWS ? (
          <p className="mt-2 text-xs text-neutral-500">
            {rows.length - MAX_ROWS} more; ask Claude to list_job_leads.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function LeadRow({ lead }: { lead: JobLead }) {
  const dismiss = useDismissLead();
  const href = lead.job_url ?? lead.external_url;
  const title = lead.job_title ?? lead.title ?? "(untitled lead)";
  return (
    <li className="flex items-center justify-between gap-2 border-t border-neutral-800 pt-2 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <p className="truncate text-sm text-neutral-200">{title}</p>
        <p className="flex items-center gap-1.5 truncate text-xs text-neutral-500">
          <span className="truncate">
            {lead.company_name ?? lead.company_id ?? "unknown company"}
            {lead.location ? ` · ${lead.location}` : ""}
            {` · ${lead.source}`}
          </span>
          {lead.status === "matched" ? <Pill tone="success">in catalog</Pill> : null}
          {lead.application_id ? (
            <Link to={`/review/${lead.application_id}`}>
              <Pill tone="info">tracked</Pill>
            </Link>
          ) : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {lead.score != null ? <ScorePill score={lead.score} /> : null}
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" leadingIcon={<ExternalLink size={12} aria-hidden />}>
              Open
            </Button>
          </a>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => dismiss.mutate(lead.id)}
          loading={dismiss.isPending}
          title="Dismiss"
          aria-label={`Dismiss lead ${title}`}
        >
          <X size={13} aria-hidden />
        </Button>
      </div>
    </li>
  );
}
